import React, { useState, useEffect } from 'react';
import { X, Save, Loader2, FileText, RefreshCw, AlertTriangle, User as UserIcon } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FIELDS = [
  // [key, labelFR, labelEN, type]
  ['date_of_birth', 'Date de naissance', 'Date of birth', 'date'],
  ['gender', 'Genre', 'Gender', 'text'],
  ['nationality', 'Nationalité', 'Nationality', 'text'],
  ['passport_number', 'N° de passeport', 'Passport number', 'text'],
  ['passport_expiry', 'Expiration du passeport', 'Passport expiry', 'date'],
  ['place_of_birth', 'Lieu de naissance', 'Place of birth', 'text'],
  ['address', 'Adresse', 'Address', 'text'],
  ['city', 'Ville', 'City', 'text'],
  ['state_province', 'Province / État', 'State / Province', 'text'],
  ['postal_code', 'Code postal', 'Postal code', 'text'],
  ['country', 'Pays', 'Country', 'text'],
  ['occupation', 'Profession', 'Occupation', 'text'],
  ['organization', 'Organisation', 'Organization', 'text'],
  ['organization_address', 'Adresse organisation', 'Organization address', 'text'],
  ['arrival_date', "Date d'arrivée", 'Arrival date', 'date'],
  ['departure_date', 'Date de départ', 'Departure date', 'date'],
  ['visa_embassy_city', 'Ambassade (ville)', 'Embassy city', 'text'],
  ['visa_country', 'Pays de visa', 'Visa country', 'text'],
  ['reason', 'Raison de participation', 'Reason for attending', 'textarea'],
  ['dietary_requirements', 'Régime alimentaire', 'Dietary requirements', 'text'],
  ['heard_about', 'Comment nous avez-vous connu ?', 'How did you hear about us?', 'text'],
  ['special_needs', 'Besoins spéciaux', 'Special needs', 'text'],
  ['emergency_contact', "Contact d'urgence", 'Emergency contact', 'text'],
  ['emergency_phone', "Tél. urgence", 'Emergency phone', 'text'],
  ['emergency_relationship', "Relation (urgence)", 'Emergency relationship', 'text'],
];

const copy = {
  fr: {
    title: "Modifier l'inscription",
    subtitle: "Corrigez les informations fournies par le participant. Les changements de nom modifient aussi son compte.",
    identitySection: "Identité du participant",
    firstName: "Prénom",
    lastName: "Nom",
    email: "Adresse courriel",
    phone: "Téléphone",
    cancel: "Annuler",
    save: "Enregistrer",
    saving: "Enregistrement…",
    success: "Inscription mise à jour avec succès.",
    genericError: "Une erreur s'est produite. Veuillez réessayer.",
    visaLetter: "Lettre d'invitation visa requise",
    reissueTitle: "Réémettre la lettre d'invitation",
    reissueSubtitle: "À utiliser après avoir corrigé une erreur (nom, date de naissance, passeport…). L'ancienne lettre est révoquée et une nouvelle est générée puis envoyée par courriel au participant.",
    reissueBtn: "Réémettre la lettre",
    reissueBusy: "Émission en cours…",
    reissueConfirm: "Confirmer : l'ancienne lettre d'invitation sera révoquée et une nouvelle sera envoyée au participant. Continuer ?",
    reissueOk: "Lettre réémise avec succès. Numéro :",
    reissueWarn: "Sauvegardez d'abord vos corrections avant de réémettre la lettre.",
  },
  en: {
    title: "Edit registration",
    subtitle: "Correct the attendee's information. Name changes also update their account.",
    identitySection: "Attendee identity",
    firstName: "First name",
    lastName: "Last name",
    email: "Email address",
    phone: "Phone",
    cancel: "Cancel",
    save: "Save changes",
    saving: "Saving…",
    success: "Registration updated successfully.",
    genericError: "Something went wrong. Please try again.",
    visaLetter: "Needs visa invitation letter",
    reissueTitle: "Reissue visa invitation letter",
    reissueSubtitle: "Use after correcting a mistake (name, date of birth, passport…). The old letter is revoked and a fresh one is generated and emailed to the attendee.",
    reissueBtn: "Reissue letter",
    reissueBusy: "Reissuing…",
    reissueConfirm: "Confirm: the old invitation letter will be revoked and a new one will be sent to the attendee. Continue?",
    reissueOk: "Letter reissued successfully. Number:",
    reissueWarn: "Save your corrections first before reissuing the letter.",
  },
};

export default function AdminEditRegistrationModal({ open, onClose, registration, attendee, token, language = 'fr', onSaved }) {
  const t = copy[language] || copy.fr;
  const [form, setForm] = useState({});
  const [identity, setIdentity] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const [reissuing, setReissuing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (open && registration) {
      const initial = { needs_visa_letter: !!registration.needs_visa_letter };
      FIELDS.forEach(([k]) => { initial[k] = registration[k] || ''; });
      setForm(initial);
      const u = attendee || registration.user || {};
      setIdentity({
        first_name: u.first_name || '',
        last_name: u.last_name || '',
        email: u.email || '',
        phone: u.phone || '',
      });
      setError('');
      setSuccess('');
      setDirty(false);
    }
  }, [open, registration, attendee]);

  if (!open || !registration) return null;

  const userId = (attendee || registration.user || {}).id || registration.user_id;

  const handleChange = (field) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [field]: val }));
    setDirty(true);
  };

  const handleIdentityChange = (field) => (e) => {
    setIdentity((it) => ({ ...it, [field]: e.target.value }));
    setDirty(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      // Registration payload
      const regPayload = {};
      Object.keys(form).forEach((k) => {
        const v = form[k];
        if (typeof v === 'boolean') regPayload[k] = v;
        else if (v !== '' && v !== null && v !== undefined) regPayload[k] = v;
      });

      // User identity payload (only if any field is non-empty)
      const userPayload = {};
      ['first_name', 'last_name', 'email', 'phone'].forEach((k) => {
        if (identity[k] && identity[k].trim() !== '') userPayload[k] = identity[k].trim();
      });

      const requests = [];
      if (Object.keys(regPayload).length) {
        requests.push(axios.patch(`${BACKEND_URL}/api/admin/registrations/${registration.id}`, regPayload, { headers: { Authorization: `Bearer ${token}` } }));
      }
      if (userId && Object.keys(userPayload).length) {
        requests.push(axios.patch(`${BACKEND_URL}/api/admin/users/${userId}`, userPayload, { headers: { Authorization: `Bearer ${token}` } }));
      }
      const results = await Promise.all(requests);
      const updatedReg = results.find(r => r.data?.id === registration.id)?.data;
      const updatedUser = results.find(r => r.data?.id === userId)?.data;

      setSuccess(t.success);
      setDirty(false);
      if (onSaved) {
        const merged = { ...(updatedReg || registration), ...regPayload };
        if (updatedUser) merged.user = updatedUser;
        onSaved(merged);
      }
      // Don't auto-close — admin may want to chain a "Reissue" action right after
    } catch (err) {
      setError(err?.response?.data?.detail || t.genericError);
    } finally {
      setSaving(false);
    }
  };

  const handleReissue = async () => {
    if (dirty) {
      setError(t.reissueWarn);
      return;
    }
    if (!window.confirm(t.reissueConfirm)) return;
    setReissuing(true);
    setError('');
    setSuccess('');
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/registrations/${registration.id}/reissue-invitation`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(`${t.reissueOk} ${res.data?.invitation_number || ''}`);
      if (onSaved) onSaved({ ...registration, invitation_status: 'sent', invitation_reissued_at: new Date().toISOString() });
    } catch (err) {
      setError(err?.response?.data?.detail || t.genericError);
    } finally {
      setReissuing(false);
    }
  };

  const canReissue = ['paid', 'approved'].includes(registration.status) || registration.payment_exempted;

  return (
    <div
      data-testid="admin-edit-registration-modal"
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div>
            <h2 className="text-lg font-extrabold text-[#1D1D1D] flex items-center gap-2">
              <FileText className="w-4 h-4 text-[#CD0000]" /> {t.title}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">{t.subtitle}</p>
          </div>
          <button type="button" onClick={onClose} data-testid="admin-edit-registration-close" className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6" data-testid="admin-edit-registration-form">
          {/* Attendee identity (User table) */}
          <div className="bg-gradient-to-br from-amber-50 to-amber-100/30 border border-amber-200 rounded-2xl p-4">
            <h3 className="text-xs font-extrabold text-amber-900 uppercase tracking-wider mb-3 flex items-center gap-2">
              <UserIcon className="w-3.5 h-3.5" /> {t.identitySection}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-semibold text-amber-800 mb-1">{t.firstName}</label>
                <input
                  type="text" value={identity.first_name} onChange={handleIdentityChange('first_name')}
                  data-testid="admin-edit-attendee-first-name"
                  className="w-full bg-white border border-amber-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-500"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-amber-800 mb-1">{t.lastName}</label>
                <input
                  type="text" value={identity.last_name} onChange={handleIdentityChange('last_name')}
                  data-testid="admin-edit-attendee-last-name"
                  className="w-full bg-white border border-amber-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-500"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-amber-800 mb-1">{t.email}</label>
                <input
                  type="email" value={identity.email} onChange={handleIdentityChange('email')}
                  data-testid="admin-edit-attendee-email"
                  className="w-full bg-white border border-amber-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-500"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-amber-800 mb-1">{t.phone}</label>
                <input
                  type="tel" value={identity.phone} onChange={handleIdentityChange('phone')}
                  data-testid="admin-edit-attendee-phone"
                  className="w-full bg-white border border-amber-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-500"
                />
              </div>
            </div>
          </div>

          {/* Registration fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {FIELDS.map(([key, , , type]) => (
              <div key={key} className={type === 'textarea' ? 'md:col-span-2' : ''}>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5">
                  {(language === 'fr' ? FIELDS.find(f => f[0] === key)[1] : FIELDS.find(f => f[0] === key)[2])}
                </label>
                {type === 'textarea' ? (
                  <textarea
                    value={form[key] || ''} onChange={handleChange(key)} rows={3}
                    className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#CD0000]"
                  />
                ) : (
                  <input
                    type={type} value={form[key] || ''} onChange={handleChange(key)}
                    data-testid={`admin-edit-reg-${key}`}
                    className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#CD0000]"
                  />
                )}
              </div>
            ))}

            <label className="md:col-span-2 flex items-center gap-3 p-3 border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50">
              <input
                type="checkbox" checked={!!form.needs_visa_letter} onChange={handleChange('needs_visa_letter')}
                className="w-4 h-4 accent-[#CD0000]"
              />
              <span className="text-sm text-gray-700">{t.visaLetter}</span>
            </label>
          </div>

          {/* Reissue Invitation block */}
          {canReissue && (
            <div className="border-2 border-dashed border-amber-300 rounded-2xl p-4 bg-amber-50/40" data-testid="admin-reissue-invitation-block">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                  <RefreshCw className="w-4 h-4 text-amber-700" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-extrabold text-amber-900">{t.reissueTitle}</h3>
                  <p className="text-xs text-amber-800 mt-1 leading-relaxed">{t.reissueSubtitle}</p>
                  {dirty && (
                    <p className="text-[11px] text-amber-700 mt-2 flex items-center gap-1.5 font-bold">
                      <AlertTriangle className="w-3.5 h-3.5" /> {t.reissueWarn}
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={handleReissue}
                    disabled={reissuing || dirty}
                    data-testid="admin-reissue-invitation-btn"
                    className="mt-3 inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold text-xs px-3.5 py-2 rounded-lg transition"
                  >
                    {reissuing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                    {reissuing ? t.reissueBusy : t.reissueBtn}
                  </button>
                </div>
              </div>
            </div>
          )}

          {error && <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>}
          {success && <p className="text-sm text-green-700 bg-green-50 border border-green-100 rounded-lg px-3 py-2">{success}</p>}
        </form>

        <div className="flex gap-2 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl shrink-0">
          <button type="button" onClick={onClose} className="flex-1 bg-white border border-gray-200 hover:bg-gray-100 text-gray-700 font-semibold py-2.5 rounded-xl transition">{t.cancel}</button>
          <button
            type="submit" onClick={handleSubmit} disabled={saving}
            data-testid="admin-edit-registration-submit"
            className="flex-1 bg-[#CD0000] hover:bg-[#A00000] disabled:opacity-60 text-white font-semibold py-2.5 rounded-xl transition flex items-center justify-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? t.saving : t.save}
          </button>
        </div>
      </div>
    </div>
  );
}
